<template>
  <div class="modal fade" id="createCompetitionBonusModalForm" tabindex="-1" aria-labelledby="createCompetitionBonusModalFormLabel" aria-hidden="true">
    <div class="modal-dialog">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title" id="createCompetitionBonusModalFormLabel">Create Competition Bonus</h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
        </div>
        <div class="modal-body">
          <div v-if="loading" class="text-center my-4">
            <div class="spinner-border" role="status">
              <span class="sr-only">&nbsp;&nbsp;Loading...</span>
            </div>
          </div>
          <div v-else>
            <form @submit.prevent="submitForm">
              <div class="mb-3">
                <label for="bonus_title" class="form-label">Title</label>
                <input type="text" class="form-control" id="bonus_title" v-model="localBonus.title" required>
              </div>
              <div class="mb-3">
                <label for="bonus_subtask" class="form-label">Subtask</label>
                <select class="form-control" id="bonus_subtask" v-model.number="localBonus.subtask_id" required>
                  <option disabled value="">-- Select subtask --</option>
                  <option v-for="st in subtasks" :key="st.id" :value="st.id">{{ st.number }} - {{ st.title }}</option>
                </select>
              </div>
              <div class="mb-3">
                <label for="bonus_prefix" class="form-label">Prefix</label>
                <input type="text" class="form-control" id="bonus_prefix" v-model="localBonus.prefix" required>
              </div>
              <div class="row g-2">
                <div class="col-md-6">
                  <label for="bonus_metric" class="form-label">Metric</label>
                  <select class="form-select" id="bonus_metric" v-model="localBonus.metric">
                    <option v-for="m in metricValues" :key="m" :value="m">{{ m }}</option>
                  </select>
                </div>
                <div class="col-md-3">
                  <label for="bonus_top_k" class="form-label text-nowrap">
                      Top students
                      <span
                        class="text-muted"
                        v-tooltip="'Number of best students who will receive the bonus.'"
                      >
                        <i class="bi bi-question-circle"></i>
                      </span>
                  </label>
                  <input type="number" min="1" class="form-control" id="bonus_top_k" v-model.number="localBonus.top_k" required>
                </div>
                <div class="col-md-3">
                  <label for="bonus_points" class="form-label">Points</label>
                  <input type="number" min="0" class="form-control" id="bonus_points" v-model.number="localBonus.points" required>
                </div>
              </div>
              <div class="modal-footer mt-3">
                <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Close</button>
                <button type="submit" class="btn btn-primary">Create</button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  </div>

  <StemErrorModal ref="stemErrorModal" />
</template>

<script>
import { Modal } from 'bootstrap';
import HomeworkController from '@/controllers/HomeworkController';
import StemErrorModal from './StemErrorModal.vue';
import { CompetitionMetric } from '@/models/competition';

export default {
  name: 'CreateCompetitionBonusModalForm',
  components: { StemErrorModal },
  props: {
    homework: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      loading: false,
      subtasks: [],
      localBonus: this.getDefaultBonus(),
    };
  },
  watch: {
    homework: {
      immediate: true,
      async handler(hw) {
        if (hw && hw.id) {
          await this.fetchSubtasks(hw.id);
        } else {
          this.subtasks = [];
        }
        this.resetForm();
      },
    },
  },
  mounted() {
    const modalElement = document.getElementById('createCompetitionBonusModalForm');
    if (modalElement) {
      modalElement.addEventListener('hidden.bs.modal', this.resetForm);
    }
  },
  computed: {
    metricValues() {
      return CompetitionMetric.values();
    },
  },
  methods: {
    getDefaultBonus() {
      return {
        title: '',
        subtask_id: '',
        prefix: '',
        metric: CompetitionMetric.ASC,
        top_k: 1,
        points: 0,
      };
    },
    resetForm() {
      this.localBonus = this.getDefaultBonus();
    },
    async fetchSubtasks(homeworkId) {
      try {
        this.loading = true;
        const hw = await HomeworkController.fetchHomeworkByIdWithRelatedData(homeworkId);
        this.subtasks = Array.isArray(hw.subtasks) ? hw.subtasks.slice().sort((a, b) => a.number - b.number) : [];
      } catch (error) {
        this.$refs.stemErrorModal.show('Error', error.message);
      } finally {
        this.loading = false;
      }
    },
    submitForm() {
      // Basic sanitize
      if (!this.localBonus.title || !this.localBonus.subtask_id || !this.localBonus.prefix) {
        this.$refs.stemErrorModal.show('Error', 'Please fill out all required fields.');
        return;
      }
      if (!Number.isInteger(this.localBonus.points) || this.localBonus.points < 0) this.localBonus.points = 0;
      if (!Number.isInteger(this.localBonus.top_k) || this.localBonus.top_k < 1) this.localBonus.top_k = 1;
      if (!this.metricValues.includes(this.localBonus.metric)) this.localBonus.metric = CompetitionMetric.ASC;

      // Emit to parent; parent will call API
      this.$emit('submitForm', { ...this.localBonus });
      
      // Close modal after successful validation
      const modalElement = document.getElementById('createCompetitionBonusModalForm');
      const modalInstance = Modal.getInstance(modalElement) || new Modal(modalElement);
      if (modalInstance) modalInstance.hide();
    },
  },
};
</script>
